import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subMonths, addMonths, format } from 'date-fns';
import ScheduleService from '../../../AppSettings/services/ScheduleService';
import MilestoneService, {
  type MilestoneRow,
} from '../../../AppSettings/services/MilestoneService';
import ToastService from '../../../AppSettings/services/ToastService';
import { isEngineeringEnvironment } from '../../../AppSettings/environmentConfig';
import ScheduleFilters from './Filters/ScheduleFilters';
import ScheduleTimeline from './Timeline/ScheduleTimeline';
import ScheduleLegend from './Legend/ScheduleLegend';
import ScheduleExportModal, { type ExportOptions } from './Export/ScheduleExportModal';
import { exportAsPNG, exportAsPDF } from './Export/scheduleExportUtils';
import ScheduleTableView from './Table/ScheduleTableView';
import type {
  ScheduleAggregateFilters,
  ScheduleProjectSummary,
} from '../../../types/workflows';
import './SchedulePage.css';

/**
 * Schedule / Gantt View Page
 * 
 * PRD4: Cross-Project Schedule / Gantt View
 * This page provides a timeline/Gantt-style visualization of multiple projects and their milestones.
 * 
 * Features:
 * - View projects with their milestone timelines
 * - Filter by discipline, status, client, date range
 * - Hover tooltips with project details
 * - Click to drill into project details
 * - Export schedule as image/PDF
 * 
 * Current Step: Base page shell with loading/error/empty states and basic project list
 */

const STORAGE_KEY = 'schedule-filters';

/**
 * Get default date range: Last 3 months to next 3 months
 */
const getDefaultDateRange = () => {
  const today = new Date();
  const startDate = subMonths(today, 3);
  const endDate = addMonths(today, 3);
  
  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Load filters from localStorage or return defaults
 */
const loadFilters = (): ScheduleAggregateFilters => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that dates are present
      if (parsed.startDate && parsed.endDate) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
  }
  
  // Return defaults
  const dateRange = getDefaultDateRange();
  return {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  };
};

/**
 * Save filters to localStorage
 */
const saveFilters = (filters: ScheduleAggregateFilters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check if we're returning from a project details page (via query param)
  const fromProject = searchParams.get('from') === 'project';
  const returnProjectId = searchParams.get('projectId');
  
  // Filter state with defaults
  const [filters, setFilters] = useState<ScheduleAggregateFilters>(() => loadFilters());
  
  // Search state (for UI, not part of API filters yet)
  const [searchInput, setSearchInput] = useState('');
  
  // View mode: 'table' or 'timeline'
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('timeline');
  
  // Timeline settings
  const [colorMode, setColorMode] = useState<'status' | 'discipline' | 'owner'>('status');
  const [zoomLevel, setZoomLevel] = useState<'week' | 'month' | 'quarter'>('month');
  
  // Navigation loading state
  const [navigatingToProject, setNavigatingToProject] = useState<string | null>(null);
  
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [milestonesByProject, setMilestonesByProject] = useState<Record<string, MilestoneRow[]>>({});
  const [milestonesLoading, setMilestonesLoading] = useState(false);

  // Fetch schedule aggregates (project-level milestone summaries)
  const {
    data: scheduleAggregates,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['schedule-aggregates', filters, page, limit],
    queryFn: () =>
      ScheduleService.getAggregates({
        filters: {
          statuses: filters.statuses,
          disciplines: filters.disciplines,
          clientIds: filters.clientIds,
          projectTypes: filters.projectTypes,
          userIds: filters.userIds,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        },
        pagination: {
          page,
          limit,
          sortBy: 'nextPlannedDate',
          sortOrder: 'asc',
        },
      }),
    staleTime: 30000, // 30 seconds
    retry: 2,
  });

  // Save filters to localStorage when they change
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Handle return from project details (restore filters if needed)
  useEffect(() => {
    if (fromProject && returnProjectId) {
      // Check if filters were preserved in query params
      const preservedFilters = searchParams.get('filters');
      if (preservedFilters) {
        try {
          const decodedFilters = JSON.parse(decodeURIComponent(preservedFilters));
          setFilters(decodedFilters);
          
          // Restore view settings if available
          const preservedViewMode = searchParams.get('viewMode');
          const preservedColorMode = searchParams.get('colorMode');
          const preservedZoomLevel = searchParams.get('zoomLevel');
          
          if (preservedViewMode && (preservedViewMode === 'table' || preservedViewMode === 'timeline')) {
            setViewMode(preservedViewMode);
          }
          if (preservedColorMode && (preservedColorMode === 'status' || preservedColorMode === 'discipline' || preservedColorMode === 'owner')) {
            setColorMode(preservedColorMode);
          }
          if (preservedZoomLevel && (preservedZoomLevel === 'week' || preservedZoomLevel === 'month' || preservedZoomLevel === 'quarter')) {
            setZoomLevel(preservedZoomLevel);
          }
          
          // Clean up query params
          searchParams.delete('from');
          searchParams.delete('projectId');
          searchParams.delete('filters');
          searchParams.delete('viewMode');
          searchParams.delete('colorMode');
          searchParams.delete('zoomLevel');
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          console.warn('Failed to restore filters from query params:', error);
        }
      }
    }
  }, [fromProject, returnProjectId, searchParams, setSearchParams]);

  /**
   * Transform API aggregates, apply local search, and expose pagination totals
   */
  const { projects, totalFilteredCount } = useMemo(() => {
    const aggregates: ScheduleProjectSummary[] =
      scheduleAggregates?.data?.aggregates || [];
    const paginationMeta = scheduleAggregates?.data?.pagination;

    // Local search (by ID or name) on the current page of results
    const searchTerm = searchInput.trim().toLowerCase();
    const searched = searchTerm
      ? aggregates.filter((project) => {
          const id = (project.projectId || '').toLowerCase();
          const name = (project.projectName || '').toLowerCase();
          return id.includes(searchTerm) || name.includes(searchTerm);
        })
      : aggregates;

    const total =
      searchTerm.length > 0
        ? searched.length
        : paginationMeta?.total ?? searched.length;

    return { projects: searched, totalFilteredCount: total };
  }, [scheduleAggregates, searchInput]);

  /**
   * Load milestones for the currently listed projects so the timeline can show
   * milestone-level bars instead of only project spans.
   * We keep it scoped to the current page to avoid excessive requests.
   */
  useEffect(() => {
    let cancelled = false;
    const loadMilestones = async () => {
      if (!projects || projects.length === 0) {
        setMilestonesByProject({});
        return;
      }
      setMilestonesLoading(true);
      try {
        const entries = await Promise.all(
          projects.map(async (project) => {
            try {
              const res = await MilestoneService.getProjectMilestones(project.projectId, {
                limit: 200,
                page: 1,
              });
              return [project.projectId, res.data?.milestones || []] as const;
            } catch (err) {
              console.error('Failed to load milestones for project', project.projectId, err);
              return [project.projectId, []] as const;
            }
          })
        );
        if (!cancelled) {
          setMilestonesByProject(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) {
          setMilestonesLoading(false);
        }
      }
    };

    loadMilestones();
    return () => {
      cancelled = true;
    };
  }, [projects]);

  // Handle errors with toast
  useEffect(() => {
    if (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to load schedule data';
      ToastService.error(errorMessage);
    }
  }, [error]);

  /**
   * Serialize filters to query string for preserving context. Pure — memoized once so the
   * memoized handleProjectClick below can list it honestly without re-creating per render.
   */
  const serializeFilters = useCallback((filterData: ScheduleAggregateFilters): string => {
    try {
      return encodeURIComponent(JSON.stringify(filterData));
    } catch (error) {
      console.warn('Failed to serialize filters:', error);
      return '';
    }
  }, []);

  /**
   * Handle project click in timeline - Navigate to project/matter details
   * with filter context preservation and error handling.
   *
   * useCallback (harvest hygiene): this is a dep of ScheduleTimeline's chart effect — a
   * fresh function every render tore the chart down and rebuilt it per render, losing the
   * user's pan/zoom (the exact bug ProjectScheduleTab memoizes its way around).
   */
  const handleProjectClick = useCallback(async (project: ScheduleProjectSummary) => {
    try {
      // Set loading state
      setNavigatingToProject(project.projectId);
      
      // Determine route based on drillTarget from backend or environment fallback
      const baseRoute = project.drillTarget?.route 
        ? project.drillTarget.route 
        : (isEngineeringEnvironment ? '/projectDetails' : '/matterDetails');
      
      // Serialize current filter context
      const filterContext = serializeFilters(filters);
      
      // Build query params to preserve context
      const queryParams = new URLSearchParams({
        from: 'schedule',
        filters: filterContext,
        viewMode,
        colorMode,
        zoomLevel,
      });
      
      // Navigate to project/matter details with context
      const targetPath = `${baseRoute}/${project.projectId}?${queryParams.toString()}`;
      
      navigate(targetPath);
      
      // Clear loading state after a short delay (navigation happens immediately)
      setTimeout(() => {
        setNavigatingToProject(null);
      }, 500);
      
    } catch (error) {
      // Handle navigation errors gracefully
      console.error('Navigation error:', error);
      ToastService.error('Failed to navigate to project details. Please try again.');
      setNavigatingToProject(null);
    }
  }, [filters, viewMode, colorMode, zoomLevel, navigate, serializeFilters]);

  /**
   * Handle table row click (same as timeline click)
   */
  const handleTableRowClick = (project: ScheduleProjectSummary) => {
    handleProjectClick(project);
  };

  /**
   * Handle export with options
   */
  const handleExport = async (options: ExportOptions) => {
    try {
      const chartElement = timelineRef.current?.querySelector('.schedule-timeline-chart');
      const legendElement = legendRef.current;

      if (!chartElement) {
        throw new Error('Chart element not found');
      }

      if (options.format === 'png') {
        await exportAsPNG(
          chartElement as HTMLElement,
          legendElement,
          filters,
          options
        );
      } else {
        await exportAsPDF(
          chartElement as HTMLElement,
          legendElement,
          filters,
          options
        );
      }

      ToastService.success('Schedule exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  return (
    <div className="schedule-page">
      <div className="schedule-content">
        {/* Filters + Export */}
        <ScheduleFilters
          filters={filters}
          onFiltersChange={setFilters}
          projects={projects}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          onExportClick={() => setShowExportModal(true)}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomLevelChange={setZoomLevel}
        />

        {(isLoading || isFetching || navigatingToProject) ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>
              {navigatingToProject
                ? 'Navigating to project details...'
                : 'Loading schedule data...'}
            </p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Error Loading Schedule</h3>
            <p>{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No Projects Found</h3>
            <p>No projects match your current filters. Try adjusting your date range or filter criteria.</p>
            <button
              className="btn btn-secondary"
              onClick={() => {
                const defaults = getDefaultDateRange();
                setFilters({
                  startDate: defaults.startDate,
                  endDate: defaults.endDate,
                });
                setPage(1);
              }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <>
            <div ref={legendRef}>
              <ScheduleLegend
                projects={projects}
                colorMode={colorMode}
                onColorModeChange={setColorMode}
                milestonesByProject={milestonesByProject}
              />
            </div>

            {/* Timeline or Table View */}
            {viewMode === 'timeline' ? (
              <div className="schedule-timeline-wrapper" ref={timelineRef}>
                <ScheduleTimeline
                  projects={projects}
                  milestonesByProject={milestonesByProject}
                  onProjectClick={handleProjectClick}
                  colorMode={colorMode}
                  zoomLevel={zoomLevel}
                  onZoomChange={setZoomLevel}
                  isLoading={milestonesLoading}
                />
              </div>
            ) : (
              <ScheduleTableView
                projects={projects}
                totalFilteredCount={totalFilteredCount}
                page={page}
                limit={limit}
                navigatingToProject={navigatingToProject}
                onRowClick={handleTableRowClick}
                onPageChange={setPage}
                milestonesByProject={milestonesByProject}
              />
            )}
          </>
        )}
      </div>

      {/* Export Modal */}
      <ScheduleExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        filters={filters}
        projectCount={projects.length}
      />
    </div>
  );
};

export default SchedulePage;

